import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Confirm, GetListResult, useDataProvider, useNotify } from 'react-admin';
import { CellKeyDownArgs, CellKeyboardEvent, FormatterProps, textEditor } from 'react-data-grid';
import 'react-data-grid/lib/styles.css';

import { AppContext } from '@app/contexts';
import { useErrorHandling } from '@app/hooks/useErrorHandling';
import { CASLAction, useAbility } from '@app/utils/authUtils';
import { getDateStringWithTimeZone } from '@app/utils/dateUtils';
import {
  ContextMenuProps,
  ErrorInterface,
  Row,
  addNewSegment,
  formatBackendResponse,
  getCellClass,
  getEpgErrors,
  getEpgPayload,
} from '@app/utils/epgUtils';
import { getFieldOnMainLanguage } from '@app/utils/languages';
import { makeCommaSeparatedString } from '@app/utils/stringUtils';
import { Dayjs } from 'dayjs';

import { EPGDataGrid } from '@app/components/EPGDataGrid';
import { AutoCompleteEditor } from '@app/components/EPGDataGrid/AutoCompleteEditor';
import { SelectEditor } from '@app/components/EPGDataGrid/SelectEditor';
import { SelectFormatter } from '@app/components/EPGDataGrid/SelectFormatter/SelectFormatter';
import { TimeInputEditor } from '@app/components/EPGDataGrid/TimeInputEditor';
import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { useCallbackPrompt } from '../../hooks/useCallbackPrompt';

const isTimeValueValid = (str: string) => {
  //Regex for 24 hour time format (00:00 - 23:59)
  const regex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return regex.test(str);
};

export const ListEPG = () => {
  const { handleChangeLoading } = useContext(AppContext);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [handleError] = useErrorHandling();

  const ability = useAbility();

  const [series, setSeries] = useState<GenericObject[]>([]);
  const [experts, setExperts] = useState<GenericObject[]>([]);
  const [segments, setSegments] = useState<GenericObject[]>([]);
  const [date, setDate] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [errors, setErrors] = useState<ErrorInterface[]>([]);

  const isDirty = () => {
    return JSON.stringify(nonDeletedRows) !== JSON.stringify(initialRows);
  };

  const fetchRecord = useCallback(
    (reference: string, setFunc: (value: GenericObject[]) => void) => {
      return dataProvider
        .getList(reference, {
          pagination: { page: 0, perPage: 2000 },
          sort: { field: '', order: '' },
          filter: {},
        })
        .then(({ data }) => {
          setFunc(data);
        });
    },
    [dataProvider]
  );

  const fetchSegments = useCallback(() => {
    return dataProvider.fetchSegments('segments').then((response: GetListResult) => {
      setSegments(response.data);
    });
  }, [dataProvider]);

  const fetchRecords = useCallback(async () => {
    handleChangeLoading(true);
    await fetchRecord('series', setSeries);
    await fetchRecord('experts', setExperts);
    await fetchSegments();
    handleChangeLoading(false);
  }, [fetchRecord, fetchSegments, handleChangeLoading]);

  const fetchEpgByDate = useCallback(
    async ({ date_target }: GenericObject) => {
      handleChangeLoading(true);
      const response = await dataProvider.fetchEpgByDate('epgs', date_target);
      setDate(date_target as string);
      setInitialRows((response?.data || []).filter((r: Row) => !r.is_deleted).map(formatBackendResponse));
      setRows(
        response?.data
          ? response?.data?.map((epg: GenericObject) => {
              return formatBackendResponse(epg);
            })
          : []
      );
      handleChangeLoading(false);
    },
    [dataProvider, handleChangeLoading]
  );

  const onCellKeyDown = (args: CellKeyDownArgs<Row>, e: CellKeyboardEvent) => {
    const keyCode = e.code;

    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.stopPropagation();
    }

    //Delete cell when user press backspace or delete
    if (keyCode === 'Delete') {
      e.stopPropagation();
      const row = args.row;
      const column: unknown = args.column;
      const rowsCopy = JSON.parse(JSON.stringify(rows));
      const currentRow = rowsCopy.find((item: Row) => item.id === row.id);
      currentRow[(column as GenericObject).key as string] = (column as GenericObject).defaultValue ?? '';

      setRows([...rowsCopy]);
    }
  };

  const deleteRow = (contextMenuProps: ContextMenuProps) => {
    const rowId = contextMenuProps.rowId;
    const rowsCopy = JSON.parse(JSON.stringify(rows));
    const row = rowsCopy.find((item: Row) => item.id === rowId);

    if (row.fromBackend) {
      row.is_deleted = true;
      setRows([...rowsCopy]);
    } else {
      setRows(rowsCopy.filter((item: Row) => item.id !== rowId));
    }
  };

  const onAddNew = () => {
    setRows((oldRows: Row[]) => {
      return addNewSegment(oldRows);
    });
  };

  const onSave = async () => {
    const epgErrors = getEpgErrors(rows, columns);
    setErrors(epgErrors);
    if (epgErrors.length) {
      notify('Please fill all the mandatory fields highlighted in red and try again', { type: 'error' });
      return;
    } else {
      try {
        handleChangeLoading(true);
        const response = await dataProvider.saveEpg('epgs', {
          data: getEpgPayload(rows, date),
          date_target: date,
        });
        setInitialRows((response?.data || []).filter((r: Row) => !r.is_deleted).map(formatBackendResponse));
        response?.data &&
          setRows(
            response.data.map((item: GenericObject) => {
              return formatBackendResponse(item);
            })
          );
        notify('EPG saved with success!', { type: 'success' });
      } catch (e: any) {
        handleError(e);
      } finally {
        handleChangeLoading(false);
      }
    }
  };

  const handleCopySchedule = async (fromDate: Dayjs | null) => {
    try {
      handleChangeLoading(true);
      await dataProvider.copyEpgSchedule('epgs', {
        date_source: getDateStringWithTimeZone(fromDate),
        date_target: date,
      });
      fetchEpgByDate({ date_target: date });
    } catch (e: any) {
      handleError(e);
    } finally {
      handleChangeLoading(false);
    }
  };

  const columns = [
    {
      key: 'start_time',
      name: 'START TIME *',
      editor: TimeInputEditor,
      required: true,
      cellClass: (row: Row) => {
        return getCellClass('start_time', row, errors);
      },
      formatter: (props: FormatterProps<Row>) => {
        const value = props.row.start_time;
        return <span>{isTimeValueValid(value) ? value : ''}</span>;
      },
    },
    {
      key: 'end_time',
      name: 'END TIME *',
      required: true,
      editor: TimeInputEditor,
      cellClass: (row: Row) => {
        return getCellClass('end_time', row, errors);
      },
      formatter: (props: FormatterProps<Row>) => {
        const value = props.row.end_time;
        return <span>{isTimeValueValid(value) ? value : ''}</span>;
      },
    },
    {
      key: 'segment',
      name: 'SEGMENT *',
      required: true,
      editor: SelectEditor,
      width: '10vw',
      source: segments,
      defaultValue: undefined,
      label: 'name',
      cellClass: (row: Row) => {
        return getCellClass('segment', row, errors);
      },
      formatter: (props: FormatterProps<Row>) => {
        const value = props.row.segment as GenericObject;
        return <SelectFormatter value={value} />;
      },
    },
    {
      key: 'series',
      name: 'SERIES *',
      width: '10vw',
      required: true,
      editor: SelectEditor,
      source: series,
      defaultValue: undefined,
      label: 'name',
      cellClass: (row: Row) => {
        return getCellClass('series', row, errors);
      },
      formatter: (props: FormatterProps<Row>) => {
        const value = props.row.series as GenericObject;
        return <SelectFormatter value={value} />;
      },
    },
    { key: 'guests', name: 'GUESTS', editor: textEditor },
    {
      key: 'experts',
      name: 'EXPERTS',
      width: '30vw',
      editor: AutoCompleteEditor,
      source: experts,
      defaultValue: [],
      formatter: (props: FormatterProps<Row>) => {
        const value = props.row.experts;
        const namesArray = value?.map(
          (item: GenericObject) =>
            `${getFieldOnMainLanguage(item.first_name)} ${getFieldOnMainLanguage(item.last_name)}`
        );
        const namesString = makeCommaSeparatedString(namesArray);

        return <span>{namesString} </span>;
      },
      getLabel: (item: GenericObject) =>
        `${getFieldOnMainLanguage(item.first_name)} ${getFieldOnMainLanguage(item.last_name)}`,
    },
  ];

  if (ability.cannot(CASLAction.WRITE, 'epgs')) {
    columns.forEach((column) => {
      // @ts-ignore "editable" is a valid prop for the
      // react-data-grid element inside EPGDataGrid,
      // which 'columns' is fed into.
      // see: https://mui.com/x/react-data-grid/editing/
      column['editable'] = false;
    });
  }

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const [initialRows, setInitialRows] = useState<Row[]>([]);
  const nonDeletedRows = useMemo(() => rows.filter((item) => !item.is_deleted), [rows]);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(isDirty());

  return (
    <>
      <Confirm
        isOpen={showPrompt as boolean}
        title="Your changes haven't been saved"
        content="Are you sure you want to leave this page without saving?"
        onConfirm={confirmNavigation as () => void}
        cancel="Keep editing"
        confirm="Close without saving"
        onClose={cancelNavigation as () => void}
      />
      <EPGDataGrid
        columns={columns}
        rows={nonDeletedRows}
        fetchEpgByDate={fetchEpgByDate}
        onAddNew={onAddNew}
        handleCopySchedule={handleCopySchedule}
        onSave={onSave}
        onCellKeyDown={onCellKeyDown}
        setRows={setRows}
        deleteRow={deleteRow}
      />
    </>
  );
};
